import { useAuth } from '@/composables/useAuth'
import { useCentrifugo } from '@/composables/useCentrifugo'
import store from '@/store'
import { createBoundedTtlSet } from '@/utils/boundedTtlSet'
import { notificationService } from '@/utils/notifications'

const DEDUP_TTL_MS = 30 * 60 * 1000
const processedNotificationIds = createBoundedTtlSet({
    ttlMs: DEDUP_TTL_MS,
    maxSize: 5000,
})

const NOTIFICATION_TYPES = {
    INFO: 'info',
    WARN: 'warn',
    ERROR: 'error',
}

const NOTIFICATION_TYPE_SET = new Set(Object.values(NOTIFICATION_TYPES))

const NOTIFICATION_HANDLERS = {
    [NOTIFICATION_TYPES.INFO]: (message, title) =>
        notificationService.showSuccess(message, title),
    [NOTIFICATION_TYPES.WARN]: (message, title) =>
        notificationService.showWarning(message, title),
    [NOTIFICATION_TYPES.ERROR]: (error, title) =>
        notificationService.showError(new Error(error), title),
}

const ALLOWED_OPERATIONS = ['print_report']
let activeChannel = null

const isObject = value =>
    value !== null && typeof value === 'object' && !Array.isArray(value)

const isValidNotification = data => {
    if (!isObject(data)) return false
    if (!NOTIFICATION_TYPE_SET.has(data.type)) return false
    if (typeof data.message !== 'string' || !data.message.trim()) return false
    return true
}

export function useUserNotifications() {
    const { user } = useAuth()
    const { connect, subscribe, unsubscribe } = useCentrifugo()

    const handlePublication = async data => {
        const consumed = await store.dispatch(
            'orders/handleUserOrderEvent',
            data
        )
        if (consumed) return

        if (!isValidNotification(data)) {
            console.warn(
                '[WS: User] Пропущено некорректное уведомление. Структура не валидна.'
            )
            if (import.meta.env.DEV) {
                console.log('[WS: User] Невалидные данные:', data)
            }
            return
        }

        const { type, message, title, data: payload } = data
        const handler = NOTIFICATION_HANDLERS[type]
        if (!handler) {
            console.warn('[WS: User] Неизвестный тип уведомления:', type)
            return
        }

        if (
            payload?.operation &&
            !ALLOWED_OPERATIONS.includes(payload?.operation)
        ) {
            if (import.meta.env.DEV) {
                console.debug(
                    '[WS: User] Операция не в списке разрешённых:',
                    payload.operation
                )
            }
            return
        }

        // Дедупликация: по id если есть, иначе по содержимому (у легаси id нет)
        const dedupKey = data.id || `${type}:${message}`
        if (processedNotificationIds.has(dedupKey)) return
        processedNotificationIds.add(dedupKey)

        handler(message, title)
    }

    const reset = () => {
        if (activeChannel) unsubscribe(activeChannel)
        activeChannel = null
        processedNotificationIds.clear()
        store.dispatch('orders/resetUserOrderEvents')
    }

    const init = () => {
        const login = user.value?.login
        if (typeof login !== 'string' || !login.trim()) return

        store.dispatch('orders/setUserOrderEventsOwner', login)

        const channel = `eds:user:${login}`
        if (activeChannel && activeChannel !== channel) {
            unsubscribe(activeChannel)
        }
        activeChannel = channel

        connect()
        subscribe(channel, data => {
            handlePublication(data).catch(error => {
                console.error(
                    '[WS: User] Ошибка обработки события:',
                    error
                )
            })
        })
    }

    return {
        initUserNotifications: init,
        resetUserNotifications: reset,
    }
}
